import React from 'react';
import type { DriveOmfzFile } from '../../../utils/driveApi';
import { deriveHistoryMeta } from '../../../utils/driveHistoryMeta';
import { formatDate, formatBytes } from '../../../utils/driveHistoryFormat';

export interface DriveHistoryRowProps {
  file: DriveOmfzFile;
  isNew: boolean;
  isDeleting: boolean;
  onSelect: (file: DriveOmfzFile) => void;
  /** issue #138/#169: 履歴削除。指定時のみ削除ボタン (PC=ホバー / スマホ=左スワイプで露出) を描画する。 */
  onDelete?: (file: DriveOmfzFile) => void;
}

/** 左スワイプで露出、右スワイプで収納する deltaX 閾値 (px)。 */
const REVEAL_THRESHOLD = -48;
const HIDE_THRESHOLD = 24;

/**
 * issue #169: Drive 履歴 1 行。フルネームを折り返し表示 (truncate 撤廃) し、総譜/パート譜バッジと
 * 総譜時のパート (1st〜4th) を出す。メタ欠損 (過去分) は項目ごと非表示にして推測しない。
 * 削除ボタンは常設せず、PC=行ホバー / スマホ=左スワイプで露出する (誤削除防止に既存確認モーダルは維持)。
 */
const DriveHistoryRow: React.FC<DriveHistoryRowProps> = ({ file, isNew, isDeleting, onSelect, onDelete }) => {
  const { fullName, modeLabel, partLabel } = deriveHistoryMeta(file);
  const [revealed, setRevealed] = React.useState(false);
  const startX = React.useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? startX.current) - startX.current;
    if (dx < REVEAL_THRESHOLD) setRevealed(true);
    else if (dx > HIDE_THRESHOLD) setRevealed(false);
    startX.current = null;
  };

  return (
    <div
      data-testid={`history-row-${file.id}`}
      className="group relative flex items-stretch border-b border-slate-200 last:border-b-0"
      onTouchStart={onDelete ? onTouchStart : undefined}
      onTouchEnd={onDelete ? onTouchEnd : undefined}>
      <button
        type="button"
        onClick={() => onSelect(file)}
        disabled={isDeleting}
        className="flex-1 min-w-0 text-left p-3 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="font-medium text-slate-800 break-words"><span aria-hidden="true">🎵</span> {fullName}</span>
          {modeLabel && (
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              {modeLabel}
            </span>
          )}
          {partLabel && (
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
              {partLabel}
            </span>
          )}
          {isNew && (
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-accent-green text-white">
              新着
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {formatDate(file.modifiedTime)} 保存 / {formatBytes(file.size)}
        </div>
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={() => onDelete(file)}
          disabled={isDeleting}
          aria-label={`「${fullName}」を削除`}
          className={`absolute inset-y-1 right-1 flex items-center justify-center px-3 bg-white/90 text-slate-400 rounded-lg hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity ${
            revealed
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus-within:opacity-100'
          }`}>
          <span aria-hidden="true" className="text-lg">{isDeleting ? '⏳' : '🗑️'}</span>
        </button>
      )}
    </div>
  );
};

export default DriveHistoryRow;
